import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { isTableRowAnyCategoryUpdatingFromMap } from '@/components/ui-v2/table-checkbox-utils';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useWheelScrollRouting } from '@/hooks/use-wheel-scroll-routing';
import { DealItemWithVersions } from '@/lib/graphql/generated/graphql';
import { cn } from '@/lib/utils';
import { getStableSortedRowModel } from '@/lib/utils/table-stable-sort';
import { columnFiltersState } from '@/states/column-filters.state';
import { columnOrderState } from '@/states/column-order.state';
import {
  columnSortState,
  tableSortResetSignalState,
} from '@/states/column-sort.state';
import { expandedState } from '@/states/expanded-state';
import { filteredFlatRowsState } from '@/states/filtered-flat-rows.state';
import { productsMapState } from '@/states/products.state';
import {
  clearSelection,
  rowSelectionModelState,
  NONE_SELECTION,
} from '@/states/row-selection.state';
import { scrollToTableColumnIdState } from '@/states/scroll-to-table-column.state';
import { ColumnAccessor } from '@/types/columns';
import type {
  ExpandedState,
  OnChangeFn,
  ColumnDef as TanstackColumnDef,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import { ChevronDown, ChevronUp } from 'lucide-react';

import './setup-table.css';
import { SetupTableRow } from './setup-table-row';

export type ColumnVisibility = Record<ColumnAccessor, boolean>;

export type ExtendedColumnDef<T> = TanstackColumnDef<T> & {
  accessorKey: string;
  removeColumn?: () => void;
  name?: string;
};

interface TableProductsProps {
  data: DealItemWithVersions[];
  columns: ExtendedColumnDef<DealItemWithVersions>[];
  columnVisibility: ColumnVisibility;
  sidebarWidth?: number;
}

export const TableProducts = ({
  data,
  columns,
  columnVisibility,
  sidebarWidth = 0,
}: TableProductsProps) => {
  const theadRef = useRef<HTMLDivElement>(null);
  const setSelectionModel = useSetAtom(rowSelectionModelState);
  const store = useStore();
  const [columnFilters, setColumnFilters] = useAtom(columnFiltersState);
  const [columnSort, setColumnSort] = useAtom(columnSortState);
  const tableSortResetSignal = useAtomValue(tableSortResetSignalState);
  const [expandedStateValue, setExpandedState] = useAtom(expandedState);
  const isOfferLocked = useIsOfferLocked();
  const columnOrder = useAtomValue(columnOrderState);
  const productsMap = useAtomValue(productsMapState);
  const setFilteredFlatRows = useSetAtom(filteredFlatRowsState);

  // Handle expanded state changes from TanStack Table
  const handleExpandedChange: OnChangeFn<ExpandedState> = useCallback(
    (updater) => {
      setExpandedState(updater);
    },
    [setExpandedState]
  );

  // Clear row selection when offer gets locked
  useEffect(() => {
    if (isOfferLocked && store.get(rowSelectionModelState) !== NONE_SELECTION) {
      setSelectionModel(clearSelection());
    }
  }, [isOfferLocked, setSelectionModel, store]);

  const visibleColumnsCount = useMemo(() => {
    const virtualColumns = [
      '__displayMode',
      '__searchTerms',
      '__comparisonFilter',
      ColumnAccessor._productGroupKey,
    ];
    return Object.entries(columnVisibility).filter(
      ([key, isVisible]) => isVisible && !virtualColumns.includes(key)
    ).length;
  }, [columnVisibility]);

  const columnPinning = useMemo(
    () => ({
      left: [
        ColumnAccessor._checkbox,
        ColumnAccessor.position,
        ColumnAccessor.images,
        ColumnAccessor.name,
      ],
      right: visibleColumnsCount >= 8 ? [ColumnAccessor.validationErrors] : [],
    }),
    [visibleColumnsCount]
  );

  const [grouping, setGrouping] = useState<string[]>([
    ColumnAccessor._productGroupKey,
  ]);

  // Recompute sort order only on mount and when user clicks column sort icon (not when data changes)
  const lastSortingRef = useRef<string>('');
  const sortedOrderIdsRef = useRef<string[] | null>(null);
  const lastTableSortResetSignalRef = useRef(tableSortResetSignal);
  const stableSortedRowModel = useMemo(
    () =>
      getStableSortedRowModel<DealItemWithVersions>({
        lastSortingRef,
        sortedOrderIdsRef,
      }),
    []
  );

  if (tableSortResetSignal !== lastTableSortResetSignalRef.current) {
    sortedOrderIdsRef.current = null;
    lastTableSortResetSignalRef.current = tableSortResetSignal;
  }

  // When sorting by position and position values change (e.g. after moveProductsToPosition
  // subscription), invalidate the sort cache so the table reorders on the next render.
  // This runs synchronously during render, before useReactTable calls getStableSortedRowModel.
  const prevDataRef = useRef<DealItemWithVersions[]>([]);
  const isPositionSort = columnSort.some(
    (s) => s.id === ColumnAccessor.position
  );
  if (isPositionSort && data !== prevDataRef.current) {
    const positionsChanged = data.some((item) => {
      const prev = prevDataRef.current.find((p) => p.id === item.id);
      return (
        !prev || prev.activeVersion?.position !== item.activeVersion?.position
      );
    });
    if (positionsChanged) {
      sortedOrderIdsRef.current = null;
    }
  }
  prevDataRef.current = data;

  // Memoize table state to prevent unnecessary re-renders
  const tableState = useMemo(
    () => ({
      columnFilters,
      columnPinning,
      sorting: columnSort,
      columnVisibility,
      expanded: expandedStateValue ?? {},
      grouping,
      columnOrder: [
        ...columnPinning.left,
        ...columnOrder,
        ...columnPinning.right,
      ] as ColumnAccessor[],
    }),
    [
      columnFilters,
      columnOrder,
      columnPinning,
      columnSort,
      columnVisibility,
      expandedStateValue,
      grouping,
    ]
  );

  const table = useReactTable({
    data: data,
    columns: columns,
    state: tableState,
    enableColumnPinning: true,
    enableColumnFilters: true,
    enableExpanding: true,
    enableRowSelection: false,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: stableSortedRowModel,
    onExpandedChange: handleExpandedChange,
    onColumnFiltersChange: setColumnFilters,
    onGroupingChange: setGrouping,
    onSortingChange: setColumnSort,
    groupedColumnMode: false,
    autoResetExpanded: false,
    enableSorting: true,
  });

  const { rows } = table.getRowModel();
  const filteredRowModel = table.getFilteredRowModel();

  // useLayoutEffect : le parent (table.tsx) dérive columnVisibility de filteredFlatRowsState
  // Guard : filteredRowModel.flatRows est une nouvelle référence à chaque render même si les
  // rows n'ont pas changé. Sans ce guard, chaque re-render de TableProducts déclenche une
  // mise à jour de filteredFlatRowsState → re-render de table.tsx → boucle infinie.
  // On compare aussi row.original pour détecter les changements de données (ex. Object → Coupon)
  // sans changement d'ID (cas du bouton physical/digital).
  const prevFlatRowIdsRef = useRef<string[]>([]);
  const prevFlatRowOriginalsRef = useRef<DealItemWithVersions[]>([]);
  useLayoutEffect(() => {
    const flatRows = filteredRowModel.flatRows;
    const nextIds = flatRows.map((r) => r.id);
    const nextOriginals = flatRows.map((r) => r.original);
    const prevIds = prevFlatRowIdsRef.current;
    const prevOriginals = prevFlatRowOriginalsRef.current;
    if (
      nextIds.length === prevIds.length &&
      nextIds.every((id, i) => id === prevIds[i]) &&
      nextOriginals.every((orig, i) => orig === prevOriginals[i])
    ) {
      return;
    }
    prevFlatRowIdsRef.current = nextIds;
    prevFlatRowOriginalsRef.current = nextOriginals;
    setFilteredFlatRows(flatRows);
  }, [filteredRowModel.flatRows, setFilteredFlatRows]);

  // Check if any rows are expanded (for button icon)
  const hasAnyExpanded = useMemo(
    () => rows.some((row) => row.getIsExpanded()),
    [rows]
  );

  // Toggle expand/collapse all rows
  const handleToggleAllExpanded = useCallback(() => {
    // Toggle between all expanded (true) and all collapsed (false)
    setExpandedState(!hasAnyExpanded as ExpandedState);
  }, [hasAnyExpanded, setExpandedState]);
  //The virtualizer needs to know the scrollable container element
  const tableContainerRef = useRef<HTMLDivElement>(null);

  useWheelScrollRouting(tableContainerRef);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 40, //estimate row height for accurate scrollbar dragging
    getScrollElement: () => tableContainerRef.current,
    overscan: 20,
    getItemKey: (index) => rows[index]!.id,
  });

  const scrollToColumnId = useAtomValue(scrollToTableColumnIdState);
  const setScrollToColumnId = useSetAtom(scrollToTableColumnIdState);
  const didScrollToBestSellerLoadRef = useRef(false);

  useLayoutEffect(() => {
    if (
      !scrollToColumnId ||
      !tableContainerRef.current ||
      (rows.length === 0 && scrollToColumnId !== ColumnAccessor.isBestSeller)
    )
      return;

    let cancelled = false;
    const container = tableContainerRef.current;
    const targetColumnId = scrollToColumnId;

    const scroll = () => {
      if (cancelled) return;
      const cell = container.querySelector(
        `[data-table-column-id="${targetColumnId}"]`
      );
      if (cell instanceof HTMLElement) {
        cell.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'center',
        });
      }
    };

    const timeoutId = window.setTimeout(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        scroll();
        setScrollToColumnId(null);
      });
    }, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    scrollToColumnId,
    columnVisibility,
    visibleColumnsCount,
    setScrollToColumnId,
    rows.length,
  ]);

  useLayoutEffect(() => {
    if (didScrollToBestSellerLoadRef.current || !tableContainerRef.current) {
      return;
    }
    const cell = tableContainerRef.current.querySelector(
      `[data-table-column-id="${ColumnAccessor.isBestSeller}"]`
    );
    if (!(cell instanceof HTMLElement)) {
      return;
    }
    cell.scrollIntoView({
      behavior: 'instant',
      inline: 'center',
      block: 'center',
    });
    didScrollToBestSellerLoadRef.current = true;
  }, []);

  return (
    <TooltipProvider>
      <div
        className="relative h-full overflow-hidden"
        style={{
          width:
            sidebarWidth > 0 ? `calc(100% - ${sidebarWidth}px + 40px)` : '100%',
        }}
      >
        <div
          data-table-scroll-container
          className="relative h-full overflow-auto"
          ref={tableContainerRef}
        >
          {/* Table header */}

          <div className="sticky top-0 z-[1] flex bg-white" ref={theadRef}>
            {table.getHeaderGroups().map((headerGroup) => {
              const headers = headerGroup.headers;
              const lastLeftPinnedHeader = [...headers]
                .reverse()
                .find((h) => h.column.getIsPinned() === 'left');
              const firstNonLeftPinnedHeader = headers.find(
                (h) => h.column.getIsPinned() !== 'left'
              );

              return (
                <div
                  key={headerGroup.id}
                  data-table-row
                  className="flex border-b border-gray-300 bg-white"
                >
                  {/* Table row */}
                  {headers.map((header) => {
                    const isPinned = header.column.getIsPinned();
                    const isNameColumn = header.column.id === 'name';

                    return (
                      <div
                        key={header.id}
                        data-table-cell
                        data-table-column-id={header.column.id}
                        className={cn(
                          'sticky z-0 flex shrink-0 items-center p-2 text-xs font-semibold text-gray-700',
                          {
                            'z-[1] bg-white': isPinned,
                            'border-l border-gray-100':
                              !firstNonLeftPinnedHeader ||
                              header.id !== firstNonLeftPinnedHeader.id,
                            'border-r border-gray-100':
                              !!lastLeftPinnedHeader &&
                              header.id === lastLeftPinnedHeader.id,
                            'ml-auto': isPinned === 'right',
                          }
                        )}
                        style={{
                          width: header.column.getSize(),
                          top: 0,
                          left:
                            isPinned === 'left'
                              ? header.column.getStart()
                              : undefined,
                          right: isPinned === 'right' ? '0' : undefined,
                        }}
                      >
                        {/* Table cell */}
                        <div className="group w-full cursor-pointer">
                          {header.isPlaceholder ? null : (
                            <div className="flex w-full items-center justify-between">
                              <div className="flex-1">
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </div>
                              {isNameColumn && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleAllExpanded();
                                  }}
                                  className="flex-shrink-0 rounded-full bg-blue-100 p-1 text-blue-800 transition-colors hover:bg-blue-200 hover:text-gray-800"
                                  title={
                                    hasAnyExpanded
                                      ? 'Collapse all rows'
                                      : 'Expand all rows'
                                  }
                                >
                                  {hasAnyExpanded ? (
                                    <ChevronUp className="h-4 w-4" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4" />
                                  )}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Table body */}
          <section
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`, // tells scrollbar how big the table is
            }}
          >
            <div
              className={cn(
                'relative flex flex-col',
                isOfferLocked && 'cursor-not-allowed'
              )}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const row = rows[virtualRow.index]!;
                const isParent = row.subRows.length > 1;

                return (
                  <div
                    data-index={virtualRow.index} // needed for dynamic row height measurement
                    ref={rowVirtualizer.measureElement} // measure dynamic row height
                    key={virtualRow.key}
                    id={row.id}
                    className="absolute flex w-full"
                    style={{
                      transform: `translateY(${virtualRow.start}px)`, // this should always be a `style` as it changes on scroll
                    }}
                    onClick={
                      isParent
                        ? (e) => {
                            e.stopPropagation();
                            row.toggleExpanded();
                          }
                        : undefined
                    }
                  >
                    <SetupTableRow
                      row={row}
                      columnVisibility={columnVisibility}
                      isRowCategoryUpdating={isTableRowAnyCategoryUpdatingFromMap(
                        row,
                        productsMap
                      )}
                      isExpanded={row.getIsExpanded()}
                    />
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </div>
    </TooltipProvider>
  );
};
